/**
 * Onboarding im — pamja e punonjësit të ri.
 *
 * Kjo nuk është faqja e HR-së me disa fusha të fshehura. Serveri kthen vetëm
 * atë që punonjësi guxon të shohë: detyrat e tij, dokumentet e tij, përparimin
 * e procesit. Shënimet e HR-së, vlerësimet e intervistave dhe proceset e të
 * tjerëve nuk dalin kurrë nga ai endpoint — sepse t'i fshehësh te shfletuesi do
 * të thotë t'i dërgosh gjithsesi.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, LinearProgress, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PageHeader from '../components/common/PageHeader';
import myWorkService, { DOCUMENT_TYPES, documentTypeLabel, documentStatusMeta } from '../services/myWorkService';
import onboardingService from '../services/onboardingService';
import { taskMeta, dayOneText } from '../config/onboarding';

const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return isNaN(x) ? '—' : `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`;
};

const CONFIRM_STATUS = {
  not_started:       { label: 'Pa konfirmuar',        color: 'default' },
  confirmed:         { label: 'Konfirmuar',           color: 'success' },
  changes_requested: { label: 'Korrigjim i kërkuar',  color: 'warning' },
  reviewed:          { label: 'Shqyrtuar nga HR-ja',  color: 'info' },
};

export default function MyOnboardingPage() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [busy, setBusy] = useState(false);
  const [upload, setUpload] = useState(null);      // detyra për të cilën ngarkohet
  const [docType, setDocType] = useState('identity_document');
  const [file, setFile] = useState(null);
  const [dataDialog, setDataDialog] = useState(false);
  const [proposed, setProposed] = useState({ phone: '', email: '', address: '', bankAccount: '' });
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const r = await myWorkService().onboarding();
    if (r?.error) setError(r.error); else setD(r);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okText) => {
    setBusy(true); setError('');
    const r = await fn();
    setBusy(false);
    if (r?.error) { setError(r.error); return null; }
    if (okText) setFlash(okText);
    await load();
    return r;
  };

  if (loading) return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>;

  // Pa punonjës të lidhur nuk shtiret vetëshërbim: kjo është gjendje e vërtetë
  // dhe HR-ja i mbledh detyrat me dorë derisa lidhja të bëhet.
  if (d && !d.hasWorker) {
    return (
      <Box>
        <PageHeader title="Onboarding im" />
        <Alert severity="info">
          {d.message || 'Llogaria jote nuk është e lidhur me një punonjës te Stafi.'}{' '}
          Drejtoju HR-së për ta plotësuar lidhjen.
        </Alert>
      </Box>
    );
  }

  if (d && !d.instance) {
    return (
      <Box>
        <PageHeader title="Onboarding im" />
        <Alert severity="info">Nuk ke një proces onboarding aktiv.</Alert>
      </Box>
    );
  }

  const i = d.instance;
  const conf = d.dataConfirmation;
  const confMeta = CONFIRM_STATUS[conf.status] || CONFIRM_STATUS.not_started;
  const docsFor = (taskId) => d.documents.filter((x) => x.taskId === taskId);

  return (
    <Box>
      <PageHeader title="Onboarding im" subtitle="Çfarë duhet bërë para dhe gjatë ditëve të para" />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {flash && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFlash('')}>{flash}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{i.position || 'Pozita'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {[i.departmentName, i.managerName ? `menaxheri: ${i.managerName}` : null,
                  i.buddyName ? `mentori: ${i.buddyName}` : null].filter(Boolean).join(' · ')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                Dita e parë: {fmtDate(i.startDate)}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary">
                Përparimi i procesit {i.progress.completed}/{i.progress.total}
              </Typography>
              <LinearProgress variant="determinate" value={i.progress.percent} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
              <Chip size="small" sx={{ mt: 0.75, height: 20, fontSize: '.66rem' }}
                color={!i.dayOne.defined ? 'default' : i.dayOne.ready ? 'success' : 'warning'}
                label={dayOneText(i.dayOne)} />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Detyrat e mia ─────────────────────────────────────────────────── */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 800, fontSize: '.92rem', mb: 1 }}>Detyrat e mia</Typography>
          {d.tasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Nuk ke detyra të caktuara ty.</Typography>
          ) : (
            <Stack spacing={1}>
              {d.tasks.map((t) => {
                const m = taskMeta(t.status);
                const closed = ['completed', 'skipped', 'cancelled'].includes(t.status);
                const mine = docsFor(t.id);
                return (
                  <Box key={t.id} sx={{ p: 1.25, border: '1px solid', borderColor: t.overdue ? '#F59E0B' : 'divider', borderRadius: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{t.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.dueDate ? `afati ${fmtDate(t.dueDate)}` : 'pa afat'}
                          {t.overdue ? ' · me vonesë' : ''}
                          {t.required ? ' · e detyrueshme' : ' · opsionale'}
                          {t.trainingName ? ` · ${t.trainingName}` : ''}
                        </Typography>
                        {t.instructions && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>{t.instructions}</Typography>
                        )}
                        {mine.map((doc) => {
                          const sm = documentStatusMeta(doc.status);
                          return (
                            <Typography key={doc.id} variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                              {doc.originalName} — <strong>{sm.label}</strong>
                              {doc.rejectReason ? `: ${doc.rejectReason}` : ''}
                            </Typography>
                          );
                        })}
                      </Box>
                      <Chip size="small" color={m.color} variant="outlined" label={m.label} sx={{ height: 20, fontSize: '.66rem' }} />
                      {!closed && (
                        <Stack direction="row" spacing={0.5}>
                          {t.documentRequired && (
                            <Button size="small" variant="outlined" disabled={busy}
                              startIcon={<UploadFileIcon sx={{ fontSize: '15px !important' }} />}
                              onClick={() => { setUpload(t); setFile(null); }}
                              sx={{ textTransform: 'none', fontSize: '.72rem' }}>Ngarko</Button>
                          )}
                          <Button size="small" disabled={busy} sx={{ textTransform: 'none', fontSize: '.72rem' }}
                            onClick={() => run(() => onboardingService().setTaskStatus(t.id, 'completed'))}>Kryer</Button>
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* ── Dokumentet e mia ──────────────────────────────────────────────── */}
      {d.documents.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontWeight: 800, fontSize: '.92rem', mb: 1 }}>Dokumentet e mia</Typography>
            <Stack spacing={0.75}>
              {d.documents.map((doc) => {
                const sm = documentStatusMeta(doc.status);
                return (
                  <Stack key={doc.id} direction="row" spacing={1} alignItems="center"
                    sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 13 }}>{doc.originalName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {documentTypeLabel(doc.documentType)} · {fmtDate(doc.uploadedAt)}
                        {doc.rejectReason ? ` · ${doc.rejectReason}` : ''}
                      </Typography>
                    </Box>
                    <Chip size="small" color={sm.color} variant="outlined" label={sm.label} sx={{ height: 20, fontSize: '.66rem' }} />
                  </Stack>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ── Të dhënat personale ───────────────────────────────────────────── */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '.92rem' }}>Të dhënat e mia</Typography>
            <Chip size="small" color={confMeta.color} label={confMeta.label} sx={{ height: 20, fontSize: '.66rem' }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Këto të dhëna i mban Stafi. Ti i konfirmon ose propozon korrigjim — ndryshimin e bën HR-ja.
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
            {[['Email', conf.current.email], ['Telefon', conf.current.phone], ['Adresa', conf.current.address],
              ['Llogaria bankare', conf.current.bankAccountMasked || 'e paregjistruar']].map(([k, v]) => (
              <Stack key={k} direction="row" spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>{k}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{v || '—'}</Typography>
              </Stack>
            ))}
          </Stack>
          {conf.note && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>Kërkesa jote: {conf.note}</Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button size="small" variant="contained" disabled={busy}
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() => run(() => myWorkService().confirmData('confirm'), 'Faleminderit — të dhënat u konfirmuan.')}
              sx={{ textTransform: 'none' }}>Konfirmo të dhënat</Button>
            <Button size="small" variant="outlined" disabled={busy}
              onClick={() => { setProposed({ phone: '', email: '', address: '', bankAccount: '' }); setNote(''); setDataDialog(true); }}
              sx={{ textTransform: 'none' }}>Kërko korrigjim</Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Ngarkimi */}
      <Dialog open={Boolean(upload)} onClose={() => setUpload(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Ngarko dokumentin</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">{upload?.title}</Typography>
            <TextField select size="small" label="Lloji" value={docType} onChange={(e) => setDocType(e.target.value)} fullWidth>
              {DOCUMENT_TYPES.map((x) => <MenuItem key={x.code} value={x.code}>{x.label}</MenuItem>)}
            </TextField>
            <Button variant="outlined" component="label" sx={{ textTransform: 'none' }}>
              {file ? file.name : 'Zgjidh skedarin (PDF, JPG, PNG — deri 8 MB)'}
              <input hidden type="file" accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </Button>
            <Alert severity="info">Detyra kryhet pasi HR-ja ta pranojë dokumentin.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpload(null)}>Anulo</Button>
          <Button variant="contained" disabled={!file || busy}
            onClick={async () => {
              const r = await run(() => myWorkService().uploadDocument(upload.id, docType, file),
                'Dokumenti u ngarkua dhe pret shqyrtimin.');
              if (r) { setUpload(null); setFile(null); }
            }}>Ngarko</Button>
        </DialogActions>
      </Dialog>

      {/* Kërkesa për korrigjim */}
      <Dialog open={dataDialog} onClose={() => setDataDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Kërko korrigjim</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="info">
              Këto vlera nuk shkruhen te Stafi. Ato i shkojnë HR-së si propozim.
            </Alert>
            {[['phone', 'Telefoni'], ['email', 'Email-i'], ['address', 'Adresa'], ['bankAccount', 'Llogaria bankare']].map(([k, label]) => (
              <TextField key={k} size="small" label={label} fullWidth value={proposed[k]}
                onChange={(e) => setProposed((p) => ({ ...p, [k]: e.target.value }))} />
            ))}
            <Divider />
            <TextField size="small" label="Shënim" fullWidth multiline minRows={2}
              value={note} onChange={(e) => setNote(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDataDialog(false)}>Anulo</Button>
          <Button variant="contained" disabled={busy}
            onClick={async () => {
              const r = await run(() => myWorkService().confirmData('request_change', proposed, note),
                'Kërkesa iu dërgua HR-së.');
              if (r) setDataDialog(false);
            }}>Dërgo</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
